import React, { useEffect, useState, useRef } from 'react';
import {
  Card, Table, Button, Space, Input, Select, Modal, Form,
  message, Popconfirm, Row, Col, Statistic, Upload, Image,
  Empty, Tag, Popover, Descriptions,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  PictureOutlined, UploadOutlined, CopyOutlined, EyeOutlined,
} from '@ant-design/icons';
import {
  getImageList,
  getImageCategories,
  getImageDetail,
  uploadSingleImage,
  updateImageInfo,
  deleteImage,
} from '../services/adminApi';
import { getCurrentApiBaseUrl } from '../services/api';

const { Search } = Input;
const { TextArea } = Input;

const buildFullImageUrl = (url: string | null | undefined): string => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const baseUrl = getCurrentApiBaseUrl();
  const apiPrefix = '/api/v1';
  const cleanBaseUrl = baseUrl.endsWith(apiPrefix) 
    ? baseUrl.slice(0, -apiPrefix.length) 
    : baseUrl;
  return cleanBaseUrl + (url.startsWith('/') ? '' : '/') + url;
};

interface ImageItem {
  id: number;
  fileName: string;
  originalName: string;
  fileUrl: string;
  fullUrl?: string;
  fileSize: number;
  mimeType: string;
  category: string | null;
  description: string | null;
  tags: string | null;
  isActive: boolean;
  useCount: number;
  createdAt: string;
}

const ImageManagement: React.FC = () => {
  const [imageList, setImageList] = useState<ImageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 12, total: 0 });
  const [filters, setFilters] = useState<{
    category?: string;
    keyword?: string;
    isActive?: boolean;
  }>({});
  const [categories, setCategories] = useState<any[]>([]);
  const [stats, setStats] = useState({ total: 0, active: 0, totalSize: '0' });

  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadForm] = Form.useForm();
  const [uploading, setUploading] = useState(false);

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingImage, setEditingImage] = useState<ImageItem | null>(null);
  const [editForm] = Form.useForm();

  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState<ImageItem | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const fetchCategories = async () => {
    try {
      const res = await getImageCategories();
      setCategories(res.data?.data || []);
    } catch (e) {
      console.error('获取图片分类失败:', e);
    }
  };

  const fetchImageList = async (page = 1, pageSize = 12) => {
    setLoading(true);
    try {
      const res = await getImageList({
        page,
        pageSize,
        ...filters,
      });
      const data = res.data?.data;
      if (data) {
        setImageList(data.list || []);
        setPagination({
          current: data.page || 1,
          pageSize: data.pageSize || 12,
          total: data.total || 0,
        });

        const totalSize = data.list?.reduce((sum: number, item: ImageItem) => sum + item.fileSize, 0) || 0;
        setStats({
          total: data.total || 0,
          active: (data.list || []).filter((i: ImageItem) => i.isActive).length,
          totalSize: formatFileSize(totalSize),
        });
      }
    } catch (e) {
      message.error('获取图片列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchImageList();
  }, [filters]);

  const handleUpload = async (options: any) => {
    const { onSuccess, onError, file } = options;
    const formData = new FormData();
    formData.append('image', file);
    
    const category = uploadForm.getFieldValue('category');
    const description = uploadForm.getFieldValue('description');
    const tags = uploadForm.getFieldValue('tags');
    
    if (category) formData.append('category', category);
    if (description) formData.append('description', description);
    if (tags) formData.append('tags', tags);

    try {
      setUploading(true);
      const res = await uploadSingleImage(formData);
      message.success('上传成功');
      setUploadModalVisible(false);
      uploadForm.resetFields();
      fetchImageList();
      fetchCategories();
      onSuccess?.(res.data);
    } catch (e: any) {
      message.error(e.message || '上传失败');
      onError?.(e);
    } finally {
      setUploading(false);
    }
  };

  const handleEditImage = (record: ImageItem) => {
    setEditingImage(record);
    editForm.setFieldsValue({
      category: record.category || undefined,
      description: record.description || undefined,
      tags: record.tags || undefined,
      isActive: record.isActive,
    });
    setEditModalVisible(true);
  };

  const handleSubmitEdit = async () => {
    try {
      const values = await editForm.validateFields();
      if (editingImage) {
        await updateImageInfo(editingImage.id, values);
        message.success('更新成功');
        setEditModalVisible(false);
        fetchImageList();
        fetchCategories();
      }
    } catch (e: any) {
      message.error(e.message || '更新失败');
    }
  };

  const handleDeleteImage = async (id: number) => {
    try {
      await deleteImage(id);
      message.success('删除成功');
      fetchImageList();
      fetchCategories();
    } catch (e: any) {
      message.error(e.message || '删除失败');
    }
  };

  const handlePreview = (record: ImageItem) => {
    setPreviewImage(record);
    setPreviewVisible(true);
  };

  const handleCopyUrl = (url: string, fullUrl?: string) => {
    const urlToCopy = fullUrl || url;
    navigator.clipboard.writeText(urlToCopy).then(() => {
      message.success('链接已复制');
    }).catch(() => {
      message.error('复制失败');
    });
  };

  const categoryOptions = categories.map(c => ({ value: c.category, label: `${c.category} (${c.count})` }));
  const allCategoryOptions = [
    { value: undefined, label: '全部分类' },
    ...categoryOptions,
  ];

  const columns = [
    {
      title: '预览',
      dataIndex: 'fileUrl',
      key: 'preview',
      width: 120,
      render: (_: string, record: ImageItem) => (
        <div 
          style={{ 
            width: 100, 
            height: 60, 
            background: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 4,
            overflow: 'hidden',
            cursor: 'pointer',
          }}
          onClick={() => handlePreview(record)}
        >
          {record.fileUrl || record.fullUrl ? (
            <Image 
              src={buildFullImageUrl(record.fullUrl || record.fileUrl)} 
              width={100} 
              height={60} 
              style={{ objectFit: 'cover' }}
              preview={false}
              fallback="https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=placeholder%20image%20book%20cover&image_size=square_hd"
            />
          ) : (
            <PictureOutlined style={{ fontSize: 24, color: '#999' }} />
          )}
        </div>
      ),
    },
    {
      title: '原始文件名',
      dataIndex: 'originalName',
      key: 'originalName',
      width: 180,
      ellipsis: true,
    },
    {
      title: '存储文件名',
      dataIndex: 'fileName',
      key: 'fileName',
      width: 180,
      ellipsis: true,
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (v: string) => v ? <Tag color="blue">{v}</Tag> : <Tag color="default">未分类</Tag>,
    },
    {
      title: '大小',
      dataIndex: 'fileSize',
      key: 'fileSize',
      width: 100,
      render: (v: number) => formatFileSize(v),
    },
    {
      title: '使用次数',
      dataIndex: 'useCount',
      key: 'useCount',
      width: 80,
      render: (v: number) => <Tag color={v > 0 ? 'green' : 'default'}>{v}</Tag>,
    },
    {
      title: '状态',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 80,
      render: (v: boolean) => (
        <Tag color={v ? 'green' : 'red'}>{v ? '启用' : '禁用'}</Tag>
      ),
    },
    {
      title: '上传时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (v: string) => v ? new Date(v).toLocaleString('zh-CN') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 220,
      render: (_: any, record: ImageItem) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handlePreview(record)}>
            预览
          </Button>
          <Popover
            content={
              <Space direction="vertical" size="small">
                <Button 
                  type="link" 
                  size="small" 
                  onClick={() => handleCopyUrl(record.fileUrl, record.fullUrl)}
                  icon={<CopyOutlined />}
                >
                  复制完整链接
                </Button>
                <Button 
                  type="link" 
                  size="small" 
                  onClick={() => handleCopyUrl(record.fileUrl)}
                  icon={<CopyOutlined />}
                >
                  复制相对路径
                </Button>
              </Space>
            }
            title="复制链接"
            trigger="click"
          >
            <Button type="link" size="small" icon={<CopyOutlined />}>复制</Button>
          </Popover>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditImage(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除这张图片？" onConfirm={() => handleDeleteImage(record.id)} okText="确定" cancelText="取消">
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card size="small">
            <Statistic title="图片总数" value={stats.total} prefix={<PictureOutlined />} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="已启用" value={stats.active} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="当前页大小" value={stats.totalSize} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="分类数" value={categories.length} />
          </Card>
        </Col>
      </Row>

      <Card
        title="图片资源库"
        extra={
          <Space>
            <Search
              placeholder="搜索文件名/描述"
              allowClear
              style={{ width: 200 }}
              onSearch={(v) => setFilters((f) => ({ ...f, keyword: v || undefined }))}
            />
            <Select
              placeholder="分类筛选"
              allowClear
              style={{ width: 150 }}
              onChange={(v) => setFilters((f) => ({ ...f, category: v }))}
              options={allCategoryOptions}
            />
            <Select
              placeholder="状态筛选"
              allowClear
              style={{ width: 120 }}
              onChange={(v) => setFilters((f) => ({ ...f, isActive: v }))}
              options={[
                { value: true, label: '已启用' },
                { value: false, label: '已禁用' },
              ]}
            />
            <Button icon={<ReloadOutlined />} onClick={() => { fetchImageList(); fetchCategories(); }}>刷新</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setUploadModalVisible(true)}>上传图片</Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={imageList}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 张图片`,
            onChange: (page, pageSize) => fetchImageList(page, pageSize),
            pageSizeOptions: ['12', '24', '48', '100'],
          }}
          size="middle"
          scroll={{ x: 1400 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="暂无图片"
              >
                <Button type="primary" onClick={() => setUploadModalVisible(true)}>
                  上传第一张图片
                </Button>
              </Empty>
            ),
          }}
        />
      </Card>

      {/* 上传弹窗 */}
      <Modal
        title="上传图片"
        open={uploadModalVisible}
        onCancel={() => { setUploadModalVisible(false); uploadForm.resetFields(); }}
        footer={null}
        width={520}
        destroyOnClose
      >
        <Form form={uploadForm} layout="vertical">
          <Form.Item
            name="category"
            label="分类（可选）"
          >
            <Select
              placeholder="选择或输入分类"
              allowClear
              mode="tags"
              options={categoryOptions}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="描述（可选）"
          >
            <TextArea rows={2} placeholder="图片描述" />
          </Form.Item>
          <Form.Item
            name="tags"
            label="标签（可选）"
          >
            <Input placeholder="多个标签用逗号分隔" />
          </Form.Item>
          <Form.Item
            label="选择图片"
            required
          >
            <Upload
              name="image"
              customRequest={handleUpload}
              listType="picture-card"
              maxCount={1}
              accept="image/*"
              fileList={undefined}
            >
              <div>
                <div style={{ marginTop: 8 }}>
                  <UploadOutlined />
                </div>
                <div style={{ marginTop: 8 }}>点击上传</div>
              </div>
            </Upload>
            <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
              支持 JPG、PNG、GIF、WEBP 格式，单张不超过 10MB
            </div>
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑弹窗 */}
      <Modal
        title="编辑图片信息"
        open={editModalVisible}
        onOk={handleSubmitEdit}
        onCancel={() => setEditModalVisible(false)}
        okText="保存"
        cancelText="取消"
        width={520}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item
            name="category"
            label="分类"
          >
            <Select
              placeholder="选择或输入分类"
              allowClear
              mode="tags"
              options={categoryOptions}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea rows={2} placeholder="图片描述" />
          </Form.Item>
          <Form.Item
            name="tags"
            label="标签"
          >
            <Input placeholder="多个标签用逗号分隔" />
          </Form.Item>
          <Form.Item
            name="isActive"
            label="状态"
            valuePropName="checked"
          >
            <Select
              options={[
                { value: true, label: '启用' },
                { value: false, label: '禁用' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 预览弹窗 */}
      <Modal
        title="图片预览"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewVisible(false)}>关闭</Button>,
          <Button 
            key="copy" 
            type="primary" 
            icon={<CopyOutlined />}
            onClick={() => previewImage && handleCopyUrl(previewImage.fileUrl, previewImage.fullUrl)}
          >
            复制链接
          </Button>,
        ]}
        width={700}
      >
        {previewImage && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              {previewImage.fileUrl || previewImage.fullUrl ? (
                <Image
                  src={buildFullImageUrl(previewImage.fullUrl || previewImage.fileUrl)}
                  style={{ maxWidth: '100%', maxHeight: 400 }}
                  preview={true}
                  fallback="https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=placeholder%20image%20book%20cover&image_size=square_hd"
                />
              ) : (
                <div style={{ padding: 40, background: '#f5f5f5' }}>
                  <PictureOutlined style={{ fontSize: 64, color: '#999' }} />
                </div>
              )}
            </div>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="原始文件名" span={2}>
                {previewImage.originalName}
              </Descriptions.Item>
              <Descriptions.Item label="存储文件名" span={2}>
                {previewImage.fileName}
              </Descriptions.Item>
              <Descriptions.Item label="分类">
                {previewImage.category || '未分类'}
              </Descriptions.Item>
              <Descriptions.Item label="文件大小">
                {formatFileSize(previewImage.fileSize)}
              </Descriptions.Item>
              <Descriptions.Item label="使用次数">
                {previewImage.useCount}
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                {previewImage.isActive ? '启用' : '禁用'}
              </Descriptions.Item>
              <Descriptions.Item label="描述" span={2}>
                {previewImage.description || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="标签" span={2}>
                {previewImage.tags || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="相对路径" span={2}>
                <code style={{ wordBreak: 'break-all' }}>{previewImage.fileUrl}</code>
              </Descriptions.Item>
              <Descriptions.Item label="完整链接" span={2}>
                <code style={{ wordBreak: 'break-all' }}>{previewImage.fullUrl}</code>
              </Descriptions.Item>
              <Descriptions.Item label="上传时间" span={2}>
                {previewImage.createdAt ? new Date(previewImage.createdAt).toLocaleString('zh-CN') : '-'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ImageManagement;
